import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AvatarDemo = () => {
  const navigate = useNavigate();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [speechBubbleText, setSpeechBubbleText] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const audioRef = useRef(null);
  const animationRef = useRef(null);

  // Sample lesson texts
  const sampleTexts = [
    "Hello! Welcome to Kids Genie AI Academy! I'm your AI teacher, and I'm excited to help you learn today!",
    "Let's explore the amazing world of Artificial Intelligence together!",
    "Did you know that AI learns from examples, just like you do at school?",
    "Great job! You're doing wonderful! Let's continue to the next activity.",
    "Remember, every mistake is a learning opportunity. Keep practicing!"
  ];

  const speakText = async (text) => {
    if (isSpeaking) return;
    
    setIsSpeaking(true);
    setSpeechBubbleText(text);
    
    try {
      // Call backend TTS API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avatar/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text })
      });
      
      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play();
          
          // Simulate audio levels for wave animation
          animateAudioWave();
        }
      }
    } catch (error) {
      console.error('Speech error:', error);
      // Still show animation even if audio fails
      setTimeout(() => {
        setIsSpeaking(false);
        setSpeechBubbleText('');
      }, 3000);
    }
  };

  const animateAudioWave = () => {
    let level = 0;
    let direction = 1;
    
    const animate = () => {
      level += direction * 0.1;
      if (level >= 1) direction = -1;
      if (level <= 0) direction = 1;
      
      setAudioLevel(level);
      
      if (isSpeaking) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    
    animate();
  };

  const handleAudioEnd = () => {
    setIsSpeaking(false);
    setSpeechBubbleText('');
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setAudioLevel(0);
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '100px',
        height: '100px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%',
        animation: 'float 6s ease-in-out infinite'
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '20%',
        right: '15%',
        width: '150px',
        height: '150px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%',
        animation: 'float 8s ease-in-out infinite'
      }}></div>

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              background: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#667eea',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          >
            ← Back to Dashboard
          </button>

          <h1 style={{
            fontSize: '42px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '16px',
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
          }}>
            🤖 Simple Animated Avatar Demo
          </h1>
          <p style={{
            fontSize: '18px',
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Option 1: Visual feedback with animations (pulsing, glowing, audio waves)
          </p>
        </div>

        {/* Avatar Section */}
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '60px 40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative'
        }}>
          {/* Speech Bubble */}
          {speechBubbleText && (
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'white',
              border: '3px solid #667eea',
              borderRadius: '20px',
              padding: '20px 30px',
              maxWidth: '80%',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
              animation: 'fadeInDown 0.3s ease-out',
              zIndex: 10
            }}>
              <div style={{
                fontSize: '16px',
                color: '#2d3748',
                lineHeight: '1.6'
              }}>
                {speechBubbleText}
              </div>
              {/* Speech bubble pointer */}
              <div style={{
                position: 'absolute',
                bottom: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '0',
                height: '0',
                borderLeft: '12px solid transparent',
                borderRight: '12px solid transparent',
                borderTop: '12px solid #667eea'
              }}></div>
            </div>
          )}

          {/* Avatar Container */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '30px',
            marginTop: speechBubbleText ? '100px' : '0'
          }}>
            {/* Robot Genie with animations */}
            <div style={{
              position: 'relative',
              width: '300px',
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {/* Glow effect when speaking */}
              {isSpeaking && (
                <div style={{
                  position: 'absolute',
                  inset: '-20px',
                  background: `radial-gradient(circle, rgba(102, 126, 234, ${audioLevel * 0.5}) 0%, transparent 70%)`,
                  borderRadius: '50%',
                  animation: 'pulse 1s ease-in-out infinite',
                  filter: 'blur(20px)'
                }}></div>
              )}

              {/* Robot Image */}
              <img
                src="/images/robot-genie-logo.png"
                alt="Robot Genie Teacher"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  filter: isSpeaking ? 'drop-shadow(0 0 20px rgba(102, 126, 234, 0.8))' : 'none',
                  transform: isSpeaking ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.3s ease',
                  animation: isSpeaking ? 'bounce 0.5s ease-in-out infinite' : 'none'
                }}
              />
            </div>

            {/* Audio Wave Visualization */}
            {isSpeaking && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                height: '80px'
              }}>
                {[...Array(7)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '8px',
                      height: `${20 + audioLevel * 50 * Math.sin(i * 0.5)}px`,
                      background: 'linear-gradient(to top, #667eea, #764ba2)',
                      borderRadius: '4px',
                      animation: `wave 0.6s ease-in-out infinite`,
                      animationDelay: `${i * 0.1}s`
                    }}
                  ></div>
                ))}
              </div>
            )}

            {/* Status Text */}
            <div style={{
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: '600',
              color: isSpeaking ? '#667eea' : '#718096',
              minHeight: '30px'
            }}>
              {isSpeaking ? '🎤 Speaking...' : '💬 Click a button to hear me speak!'}
            </div>
          </div>

          {/* Control Buttons */}
          <div style={{
            marginTop: '40px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            maxWidth: '600px',
            margin: '40px auto 0'
          }}>
            {sampleTexts.map((text, index) => (
              <button
                key={index}
                onClick={() => speakText(text)}
                disabled={isSpeaking}
                style={{
                  padding: '16px 24px',
                  background: isSpeaking ? '#cbd5e0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isSpeaking ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: isSpeaking ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)',
                  opacity: isSpeaking ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSpeaking) e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {text.substring(0, 50)}...
              </button>
            ))}
          </div>

          {/* Custom text input */}
          <div style={{
            marginTop: '30px',
            display: 'flex',
            gap: '12px',
            maxWidth: '600px',
            margin: '30px auto 0'
          }}>
            <input
              type="text"
              value={currentText}
              onChange={(e) => setCurrentText(e.target.value)}
              placeholder="Type your own message..."
              style={{
                flex: 1,
                padding: '12px 20px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && currentText.trim()) {
                  speakText(currentText);
                  setCurrentText('');
                }
              }}
            />
            <button
              onClick={() => {
                if (currentText.trim()) {
                  speakText(currentText);
                  setCurrentText('');
                }
              }}
              disabled={isSpeaking || !currentText.trim()}
              style={{
                padding: '12px 32px',
                background: (isSpeaking || !currentText.trim()) ? '#cbd5e0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: (isSpeaking || !currentText.trim()) ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Speak
            </button>
          </div>
        </div>

        {/* Info Section */}
        <div style={{
          marginTop: '40px',
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#2d3748',
            marginBottom: '20px'
          }}>
            ✨ Features of Simple Animated Avatar:
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            <div>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>💫</div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#667eea', marginBottom: '8px' }}>
                Pulsing & Glowing
              </h3>
              <p style={{ color: '#718096', fontSize: '14px' }}>
                Avatar glows and pulses while speaking for visual feedback
              </p>
            </div>
            <div>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎵</div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#667eea', marginBottom: '8px' }}>
                Audio Waves
              </h3>
              <p style={{ color: '#718096', fontSize: '14px' }}>
                Animated wave visualization shows audio activity
              </p>
            </div>
            <div>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>💬</div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#667eea', marginBottom: '8px' }}>
                Speech Bubbles
              </h3>
              <p style={{ color: '#718096', fontSize: '14px' }}>
                Text appears in speech bubbles for better understanding
              </p>
            </div>
            <div>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚡</div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#667eea', marginBottom: '8px' }}>
                Fast & Lightweight
              </h3>
              <p style={{ color: '#718096', fontSize: '14px' }}>
                No additional costs, quick implementation, works smoothly
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnd}
        style={{ display: 'none' }}
      />

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        @keyframes bounce {
          0%, 100% { transform: scale(1.05) translateY(0); }
          50% { transform: scale(1.05) translateY(-10px); }
        }

        @keyframes wave {
          0%, 100% { transform: scaleY(0.5); }
          50% { transform: scaleY(1); }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AvatarDemo;
